import { useCallback, useState } from 'react';
import { Modal } from 'is24-corecss';
import { OverviewTab } from './components/OverviewTab';
import { HowItWorksTab } from './components/HowItWorksTab';
import FAQTab from './components/FAQTab';
import { TermsOfServiceTab } from './components/TermsOfServiceTab';
import VoucherDisplayModal from './components/VoucherDisplayModal';
import MagentaTvBannerMobileJpg from './images/MagentaTvBannerMobile.png';
import MagentaTvBannerWebp from './images/MagentaTvBannerDesktop.webp';
import MagentaTvBannerJpg from './images/MagentaTvBannerDesktop.png';
import MagentaTvBannerMobileWebp from './images/MagentaTvBannerMobile.webp';
import { useIntl } from '@/i18n/hooks/useIntl';
import { BenefitModalProps } from '@/@types/benefitModalProps';
import { Benefit } from '@/@types/benefits';
import { ProductType } from '@/@types/product';
import { BenefitsListPageType } from '@/@types/benefitsListPage';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { campaigns } from '@/components/common/campaigns';
import { APP_CONFIG } from '@/config';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import { useReportTrackingTenant } from '@/customHooks/useReportTrackingTenant.hook';
import ModalV2 from '@/components/common/display/modal/ModalV2';

export const EXPIRY_DATE = '31.12.2026';
export const MAGENTA_TV_PRODUCT_MAPPING = {
  [BenefitsListPageType.MieterPlus.toLowerCase()]: {
    productType: ProductType.MieterPlus,
    checkoutUrl: campaigns.MAGENTA_TV_CHECKOUT_MP,
    ctaText: 'Suchen+',
    productPageUrl: `${APP_CONFIG.magentaTvProductPageUrl}?wt_mc=zz_tvmtotxx_imsc-pl01-q225`,
  },
  [BenefitsListPageType.WohnenPlus.toLowerCase()]: {
    productType: ProductType.WohnenPlus,
    checkoutUrl: campaigns.MAGENTA_TV_CHECKOUT_WP,
    ctaText: 'Wohnen+',
    productPageUrl: `${APP_CONFIG.magentaTvProductPageUrl}?wt_mc=zz_tvmtotxx_imsc-pl02-q225`,
  },
  [BenefitsListPageType.KauferPlus.toLowerCase()]: {
    productType: ProductType.KaeuferPlus,
    checkoutUrl: campaigns.MAGENTA_TV_CHECKOUT_KP,
    ctaText: 'Suchen+',
    productPageUrl: `${APP_CONFIG.magentaTvProductPageUrl}?wt_mc=zz_tvmtotxx_imsc-pl03-q225?`,
  },
};

export const MagentaTv = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isMeinPlus = useHasMeinPlus();
  const { reportTenantTracking } = useReportTrackingTenant();
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const { parentPath, currentUrl, isMFE } = useUrlState();
  const productMapping =
    MAGENTA_TV_PRODUCT_MAPPING[parentPath] || MAGENTA_TV_PRODUCT_MAPPING[BenefitsListPageType.WohnenPlus.toLowerCase()];

  const handleClaimVoucher = useCallback(() => {
    if (isMeinPlus) {
      setShowVoucherModal(true);
      report(TrackingEvents.MAGENTA_TV.DISCOUNT_CODE_SEEN, isMeinPlus);
      return;
    }
    window.location.href = `${isMFE ? campaigns.MAGENTA_TV_MFE : productMapping?.checkoutUrl}${isMFE && currentUrl ? `&returnUrl=${encodeURIComponent(currentUrl)}` : ''}`;
    reportTenantTracking({ ...TrackingEvents.TENANT.DISCOVER_WOHNEN });
  }, [isMeinPlus, isMFE, currentUrl]);

  const handleCloseVoucherModal = useCallback(() => {
    setShowVoucherModal(false);
  }, []);

  if (showVoucherModal) {
    return <VoucherDisplayModal onClose={handleCloseVoucherModal} />;
  }

  return (
    <ModalV2
      title={t({ id: 'benefits.magentaTv.headline', defaultMessage: 'MagentaTV Vorteil' })}
      isVisible={isVisible}
      image={MagentaTvBannerJpg}
      imageWebp={MagentaTvBannerWebp}
      imageMobile={MagentaTvBannerMobileJpg}
      imageMobileWebp={MagentaTvBannerMobileWebp}
      imageAlt={t({ id: 'benefits.magentaTv.modal.image', defaultMessage: 'Magenta TV' })}
      imageUseFullWidth
      tabsContent={[
        <OverviewTab key='overview' />,
        <HowItWorksTab key='howItWorks' />,
        <FAQTab key='faq' benefit='magentaTv' />,
        <TermsOfServiceTab key='terms' />,
      ]}
      benefit={Benefit.MAGENTA_TV}
      onClose={onClose}
      primaryFooterBtn={
        <Modal.FooterPrimaryBtn
          label={
            isMeinPlus
              ? t({
                  id: 'benefits.magentaTv.modal.buttonCta.claimVoucher',
                  defaultMessage: 'Gutschein holen',
                })
              : t(
                  {
                    id: 'benefits.magentaTv.modal.buttonCta.explore',
                    defaultMessage: '{product} entdecken',
                  },
                  {
                    product: productMapping?.ctaText,
                  },
                )
          }
          onClick={handleClaimVoucher}
        />
      }
    />
  );
};

export default MagentaTv;
